import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useGoogleLogin } from "@react-oauth/google";
import { signInWithCustomToken } from "firebase/auth";
import { Lock, Mail } from "lucide-react";
import { NavLink as Link } from "react-router-dom";
import { toast } from "sonner";
import { auth } from "../../firebaseConfig";
import { endpoints } from "../../hooks/endpoints";
import { useAppDispatch } from "../../store/hooks";
import { userData } from "../../store/slices/storeSlice";
import Spinner from "../Spinner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const LoginForm = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoginPending, setIsLoginPending] = useState(false);
  const [isGoogleWindowOpen, setIsGoogleWindowOpen] = useState(false);
  const [isLoginWithFacebook, setIsLoginWithFacebook] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const handleLoginSubmit = (e) => {
    e.preventDefault();
    setIsLoginPending(true);
    const credentials = { username, password };
    fetch(endpoints.signIn, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(credentials),
    })
      .then((res) => res.json())
      .then((result) => {
        if (result.token) {
          signInWithCustomToken(auth, result.firebase_token)
            .then((userCredential) => {
              // Signed in
              setIsLoginPending(false);
              const user = userCredential.user;
              console.log({ user });
              dispatch(userData(result));
              localStorage.setItem("user", JSON.stringify(result));
              navigate("/");
            })
            .catch((error) => {
              setIsLoginPending(false);
              const errorCode = error.code;
              const errorMessage = error.message;
              throw new Error(errorMessage);
            });
        } else {
          setIsLoginPending(false);
          if (Object.keys(result)[0].includes("invalid_email")) {
            toast.error("الإيميل مش صح");
          } else if (Object.keys(result)[0].includes("invalid_username")) {
            toast.error("الرقم مش صح");
          } else if (Object.keys(result)[0].includes("password")) {
            toast.error("كلمة السر مش صح");
          }
        }
      })
      .catch((err) => {
        console.log(err.message);
        toast.error("حدث خطأ");
        setIsLoginPending(false);
      });
  };

  const validateGoogleTokenOnServer = (code, currentPageUrl) => {
    setIsLoginPending(true);
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    const raw = JSON.stringify({
      code,
      url: currentPageUrl,
    });

    fetch(endpoints.googleSocialLogin, {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    })
      .then((res) => res.json())
      .then((result) => {
        console.log(result, "credentialResponse");
        if (result.token) {
          signInWithCustomToken(auth, result.firebase_token)
            .then((userCredential) => {
              setIsLoginPending(false);
              const user = userCredential.user;
              console.log({ user });
              dispatch(userData(result));
              localStorage.setItem("user", JSON.stringify(result));
              setIsGoogleWindowOpen(false);
              navigate("/");
            })
            .catch((error) => {
              const errorCode = error.code;
              const errorMessage = error.message;
              throw new Error(errorMessage);
            });
        } else {
          toast.error("الإيميل مش صح");
          setIsGoogleWindowOpen(false);
          setIsLoginPending(false);
        }
      })
      .catch((err) => {
        console.log(err.message);
        toast.error("حدث خطأ");
        setIsGoogleWindowOpen(false);
      });
  };

  const loginWithGoogle = useGoogleLogin({
    flow: "auth-code",
    onSuccess: (GoogleResponse) => {
      console.log("Login Success:", GoogleResponse);
      validateGoogleTokenOnServer(GoogleResponse.code, window.location.origin);
    },
    onError: (error) => {
      console.log("Login Failed:", error);
      toast.error("حدث خطأ");
      setIsGoogleWindowOpen(false);
    },
    onNonOAuthError: () => {
      setIsGoogleWindowOpen(false);
    },
  });

  useEffect(() => {
    if (window.location.href.indexOf("code") !== -1) {
      const fbCode = window.location.href.split("code=")[1];
      if (!fbCode) return;
      setIsLoginPending(true);
      setIsLoginWithFacebook(true);
      fetch(endpoints.facebookSocialLogin, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fbCode,
          redirectUri: `${window.location.origin}${window.location.pathname}`,
        }),
      })
        .then((res) => res.json())
        .then((result) => {
          console.log(result, "credentialResponse");
          setIsLoginPending(false);
          setIsLoginWithFacebook(false);
          if (result.token) {
            dispatch(userData(result));
            localStorage.setItem("user", JSON.stringify(result));
            navigate("/");
          }
        })
        .catch((err) => {
          console.log(err.message);
          toast.error("حدث خطأ");
          setIsLoginWithFacebook(false);
          setIsLoginPending(false);
        });
    }
  }, [window.location.pathname]);

  return (
    <form className="space-y-4 w-full" onSubmit={handleLoginSubmit}>
      <div>
        <Label
          htmlFor="usernameOrEmail"
          className="flex items-center rtl:justify-end my-3"
        >
          التلفون او البريد الإلكتروني
          <Mail className="w-4 h-4 mr-2" />
        </Label>
        <Input
          id="usernameOrEmail"
          type="text"
          name="usernameOrEmail"
          placeholder="student@gmail.com"
          className="mt-1 rtl:text-right"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
        />
      </div>

      <div>
        <Label
          htmlFor="loginPassword"
          className="flex items-center rtl:justify-end my-3"
        >
          كلمة المرور
          <Lock className="w-4 h-4 mr-2" />
        </Label>
        <Input
          id="loginPassword"
          type="password"
          name="password"
          placeholder="أدخل كلمة المرور"
          className="mt-1 rtl:text-right"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
      </div>

      <div className="flex rtl:flex-row-reverse items-center justify-between my-5">
        <Link
          to="/password-reset"
          className="p-0 h-auto text-sm underline w-full text-right"
        >
          نسيت كلمة المرور؟
        </Link>
      </div>
      <div className="flex flex-col md:flex-row gap-3 login-button-group">
        <Button
          type="submit"
          className="grow md:max-w-1/2 md:order-2"
          size="lg"
          disabled={isLoginPending}
        >
          {isLoginPending ? <Spinner variant="button" /> : "تسجيل الدخول"}
        </Button>
        <Button
          type="button"
          size="lg"
          className="bg-[#ef523e] hover:bg-[#ef523e]/90 grow md:max-w-1/2 md:order-1"
          disabled={isGoogleWindowOpen}
          onClick={() => {
            loginWithGoogle();
            setIsGoogleWindowOpen(true);
          }}
        >
          <img
            style={{ margin: "0 5px", justifySelf: "center" }}
            src="https://eliteacademymedia.s3.us-east-1.amazonaws.com/qashqool_assets/media/google-icon.svg"
            alt="google"
          />{" "}
          الدخول بجوجل
        </Button>
      </div>
    </form>
  );
};

export default LoginForm;
